import { useMemo, useState } from "react";
import { Avatar } from "../../../../design_system/components/Avatar/Avatar.jsx";
import { Button, IconButton } from "../../../../design_system/components/Button/Button.jsx";
import { Icons } from "../../../../design_system/components/Icons";
import { SearchBox } from "../../../../design_system/components/Input Fields/SearchBox.jsx";
import { SelectBox } from "../../../../design_system/components/Input Fields/SelectBox.jsx";
import { Popup } from "../../../../design_system/components/Popup/Popup.jsx";
import { Toggle } from "../../../../design_system/components/SelectionControls/Toggle.jsx";
import { useToast } from "../../../../design_system/components/Toast/ToastContext.jsx";
import { updateMemberAccess } from "./api";
import {
    permissionCategoryKey,
    permissionCategoryLabel,
    permissionSubtitle,
} from "./permissionCatalogue";
import {
    buildMemberAccessPayload,
    formatMemberLastActive,
    memberRoleValue,
    reconcilePermissionOverrides,
} from "./memberAccess";

const errorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.error_message ||
    error?.response?.data?.errors?.join(", ") ||
    fallback;

const equalOverrides = (left, right) => {
    const leftKeys = Object.keys(left || {}).sort();
    const rightKeys = Object.keys(right || {}).sort();
    return leftKeys.length === rightKeys.length &&
        leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
};

const MemberAccessPopup = ({
    member,
    accessData,
    initialRoleValue,
    currentUserId,
    onClose,
    onSaved,
    onConflict,
    onRemove,
}) => {
    const toast = useToast();
    const catalogue = useMemo(
        () => accessData?.permission_catalogue || [],
        [accessData]
    );
    const customRoles = useMemo(
        () => accessData?.custom_roles || [],
        [accessData]
    );
    const categories = useMemo(
        () => accessData?.permission_categories || [],
        [accessData]
    );
    const initialRole = memberRoleValue(member);
    const initialCustomRole = initialRole.startsWith("custom:")
        ? customRoles.find((role) => role.id === initialRole.slice("custom:".length))
        : null;
    const initialOverrides = reconcilePermissionOverrides(
        member?.permission_overrides,
        initialCustomRole?.permission_keys
    );
    const startingRole = initialRoleValue || initialRole;
    const [roleValue, setRoleValue] = useState(startingRole);
    const [overrides, setOverrides] = useState(
        startingRole === initialRole ? { ...initialOverrides } : {}
    );
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [showAddException, setShowAddException] = useState(false);
    const [exceptionSearch, setExceptionSearch] = useState("");
    const [draftExceptions, setDraftExceptions] = useState({});

    const selectedCustomRole = roleValue.startsWith("custom:")
        ? customRoles.find((role) => role.id === roleValue.slice("custom:".length))
        : null;
    const roleOptions = useMemo(
        () => [
            { label: "Admin", value: "admin" },
            { label: "Regular", value: "regular" },
            ...customRoles.map((role) => ({
                label: role.name,
                value: "custom:" + role.id,
            })),
        ],
        [customRoles]
    );
    const basePermissions = useMemo(
        () => new Set(selectedCustomRole?.permission_keys || []),
        [selectedCustomRole]
    );
    const exceptionEntries = useMemo(
        () => catalogue.filter((permission) =>
            Object.prototype.hasOwnProperty.call(overrides, permission.key) &&
            overrides[permission.key] !== basePermissions.has(permission.key)
        ),
        [basePermissions, catalogue, overrides]
    );
    const roleChanged = roleValue !== initialRole;
    const dirty = roleChanged || !equalOverrides(overrides, initialOverrides);

    const payload = () => buildMemberAccessPayload(
        roleValue,
        selectedCustomRole
            ? reconcilePermissionOverrides(overrides, selectedCustomRole.permission_keys)
            : {},
        member.access_lock_version
    );

    const save = async () => {
        if (!dirty) return;
        if (roleValue.startsWith("custom:") && !selectedCustomRole) {
            setError("Select a valid custom role");
            return;
        }

        setIsSaving(true);
        setError("");
        try {
            const response = await updateMemberAccess(member.id, payload());
            toast.success("Member access updated");
            onSaved(response.member, member.user?.id === currentUserId);
        } catch (requestError) {
            const message = errorMessage(requestError, "Unable to update member access");
            setError(message);
            toast.error(message);
            if (requestError?.response?.status === 409) await onConflict?.();
        } finally {
            setIsSaving(false);
        }
    };

    const updateRole = (value) => {
        setRoleValue(value);
        setOverrides(value === initialRole ? { ...initialOverrides } : {});
        setShowAddException(false);
        setDraftExceptions({});
        setError("");
    };

    const resetOverride = (key) => {
        setOverrides((current) => {
            const next = { ...current };
            delete next[key];
            return next;
        });
    };

    const openExceptionPicker = () => {
        if (roleChanged) return;
        setExceptionSearch("");
        setDraftExceptions({});
        setShowAddException(true);
    };

    const addException = () => {
        if (Object.keys(draftExceptions).length === 0) return;
        setOverrides((current) => ({
            ...current,
            ...draftExceptions,
        }));
        setShowAddException(false);
    };

    const availablePermissions = useMemo(() => {
        const query = exceptionSearch.trim().toLowerCase();
        return catalogue.filter((permission) => {
            if (Object.prototype.hasOwnProperty.call(overrides, permission.key)) return false;
            return !query || [
                permission.label,
                permissionCategoryLabel(permission, categories),
                permission.key,
            ].some((value) => value.toLowerCase().includes(query));
        });
    }, [catalogue, categories, exceptionSearch, overrides]);

    const pickerGroups = useMemo(
        () => availablePermissions.reduce((groups, permission) => {
            const key = permissionCategoryKey(permission) + "|" + permission.kind;
            if (!groups[key]) groups[key] = [];
            groups[key].push(permission);
            return groups;
        }, {}),
        [availablePermissions]
    );

    const formattedLastActive = formatMemberLastActive(member.last_active_at);
    const drawerTitle = (
        <div className="member-access__drawer-title">
            <Avatar name={member.user?.name || member.user?.email} width={40} />
            <div>
                <div className="body-l-bold color-text-primary">
                    {member.user?.name || member.user?.email || "Member"}
                </div>
                <div className="member-access__member-meta body-s color-text-secondary">
                    <span>
                        {member.user?.email}
                        {member.status === "invited" ? " · Invited" : ""}
                    </span>
                    {formattedLastActive !== "—" && (
                        <span>Last active {formattedLastActive}</span>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="member-access__overlay" onClick={onClose}>
                <div
                    className="member-access__panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label={"Manage access for " + (member.user?.name || member.user?.email || "member")}
                    onClick={(event) => event.stopPropagation()}
                >
                <header className="member-access__panel-header">
                    {drawerTitle}
                    <IconButton
                        Size="ExtraSmall"
                        Type="Tertiary"
                        IconName="close"
                        ariaLabel="Close member access"
                        onClick={onClose}
                    />
                </header>
                <div className="member-access__panel-body">
                    <div className="member-access">
                    {member.status === "invited" && (
                        <div className="member-access__notice body-s">
                            This role is stored now. Its permissions become effective after the invite is accepted.
                        </div>
                    )}

                    <SelectBox
                        label="Role"
                        showLabel
                        value={roleValue}
                        options={roleOptions}
                        handleOnChange={updateRole}
                        Size="Large"
                        align="left"
                        containerClass="member-access__role-select"
                        customStyle={{ width: "100%" }}
                        triggerStyle={{
                            border: "1px solid var(--color-border-subtle)",
                            backgroundColor: "var(--color-surface-page)",
                        }}
                    />

                    {roleChanged && (
                        <div className="member-access__notice body-s">
                            Changing roles clears all individual exceptions. Save the new role before adding exceptions.
                        </div>
                    )}

                    {selectedCustomRole && (
                        <>
                            <div className="member-access__section-heading">
                                <div className="member-access__section-title-row">
                                    <div className="body-m-bold color-text-primary">
                                        Exceptions for {member.user?.name || "this member"}
                                    </div>
                                    <Button
                                        Size="Small"
                                        Type="Secondary"
                                        ShowLeadingIcon
                                        LeadingIcon="plus"
                                        LeadingIconProps={{ size: 16 }}
                                        onClick={openExceptionPicker}
                                        disabled={roleChanged}
                                        buttonStyle={{ flexShrink: 0 }}
                                        innerButtonStyle={{ width: "max-content", whiteSpace: "nowrap" }}
                                    >
                                        Add exception
                                    </Button>
                                </div>
                            </div>

                            <div className="member-access__exceptions">
                                {exceptionEntries.map((permission) => {
                                    const roleValueForPermission = basePermissions.has(permission.key);
                                    const overrideValue = overrides[permission.key];
                                    return (
                                        <div className="member-access__exception" key={permission.key}>
                                            <div className="member-access__exception-heading">
                                                <div>
                                                    <div className="member-access__exception-title">
                                                        <span className="body-s-bold color-text-primary">{permission.label}</span>
                                                    </div>
                                                    <div className="body-s color-text-secondary">
                                                        {permissionSubtitle(permission, categories)}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="member-access__reset"
                                                    onClick={() => resetOverride(permission.key)}
                                                >
                                                    Reset to role
                                                </button>
                                            </div>
                                            <div className="member-access__role-change body-s color-text-secondary">
                                                <span>Role gives</span>
                                                <span className="member-access__value-pill support-m">{roleValueForPermission ? "On" : "Off"}</span>
                                                <Icons name="chevron-right" size={16} />
                                                <span className="member-access__value-pill member-access__value-pill--effective support-m">
                                                    {overrideValue ? "On" : "Off"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                                {exceptionEntries.length === 0 && (
                                    <div className="member-access__empty">
                                        <Icons name="check" size={20} />
                                        <div>
                                            <div className="body-s-bold color-text-primary">No exceptions added</div>
                                            <div className="body-s color-text-secondary">
                                                This member follows the {selectedCustomRole.name} role.
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {!selectedCustomRole && (
                        <div className="member-access__notice body-s">
                            Individual exceptions are available only for custom roles.
                        </div>
                    )}

                    {error && <div className="body-s member-access__error">{error}</div>}
                    </div>
                </div>
                <footer className="member-access__panel-footer">
                    {onRemove && (
                        <button type="button" className="member-access__remove link-danger body-s-bold" onClick={onRemove} disabled={isSaving}>
                            Remove from team
                        </button>
                    )}
                    <div className="member-access__footer-actions">
                        <Button Size="Medium" Type="Secondary" onClick={onClose} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button Size="Medium" Type="Primary" onClick={save} isLoading={isSaving} disabled={!dirty}>
                            Save access
                        </Button>
                    </div>
                </footer>
                </div>
            </div>

            {showAddException && selectedCustomRole && !roleChanged && (
                <Popup
                    name={"Add exceptions for " + (member.user?.name || "this member")}
                    maxWidth={560}
                    handleOnClosePopup={() => setShowAddException(false)}
                    primaryAction={{
                        label: Object.keys(draftExceptions).length > 0
                            ? "Add " + Object.keys(draftExceptions).length + " exception" + (Object.keys(draftExceptions).length === 1 ? "" : "s")
                            : "Add exceptions",
                        onClick: addException,
                        disabled: Object.keys(draftExceptions).length === 0,
                    }}
                    secondaryAction={{
                        label: "Cancel",
                        onClick: () => setShowAddException(false),
                    }}
                >
                    <div className="member-access__picker">
                        <div className="body-s color-text-secondary">
                            Choose the permissions that should differ from the {selectedCustomRole.name} role.
                        </div>
                        <div className="member-access__picker-search">
                            <SearchBox
                                value={exceptionSearch}
                                handleOnChange={(event) => setExceptionSearch(event.target.value)}
                                placeholder="Search permissions"
                                customStyle={{ width: "100%" }}
                            />
                            <span className="support-s color-text-tertiary">
                                {availablePermissions.length} of {catalogue.length}
                            </span>
                        </div>
                        {Object.keys(draftExceptions).length > 0 && (
                            <div className="support-m color-text-secondary">
                                {Object.keys(draftExceptions).length} exception{Object.keys(draftExceptions).length === 1 ? "" : "s"} selected
                            </div>
                        )}
                        <div className="member-access__picker-results">
                            {Object.entries(pickerGroups).map(([groupKey, permissions]) => {
                                const [, kind] = groupKey.split("|");
                                const categoryLabel = permissionCategoryLabel(permissions[0], categories);
                                return (
                                    <section className="member-access__picker-group" key={groupKey}>
                                        <div className="member-access__picker-group-title support-m-caps">
                                            {categoryLabel} · {kind === "visibility" ? "Can see" : "Can do"}
                                        </div>
                                        {permissions.map((permission) => {
                                            const baseValue = basePermissions.has(permission.key);
                                            const isSelected = Object.prototype.hasOwnProperty.call(draftExceptions, permission.key);
                                            const shownValue = isSelected
                                                ? draftExceptions[permission.key]
                                                : baseValue;
                                            return (
                                                <div className="member-access__picker-row" key={permission.key}>
                                                    <div>
                                                        <div className="member-access__picker-label">
                                                            <span className="body-s-bold color-text-primary">{permission.label}</span>
                                                            {isSelected && (
                                                                <span className="member-access__exception-pill support-m">Exception</span>
                                                            )}
                                                        </div>
                                                        <div className="body-s color-text-secondary">
                                                            Role gives {baseValue ? "On" : "Off"}
                                                        </div>
                                                    </div>
                                                    <Toggle
                                                        aria-label={permission.label}
                                                        checked={shownValue}
                                                        onChange={() => {
                                                            const nextValue = !shownValue;
                                                            setDraftExceptions((current) => {
                                                                const next = { ...current };
                                                                if (nextValue === baseValue) delete next[permission.key];
                                                                else next[permission.key] = nextValue;
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </section>
                                );
                            })}
                            {availablePermissions.length === 0 && (
                                <div className="member-access__empty body-s color-text-secondary">
                                    No available permissions match this search.
                                </div>
                            )}
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
};

export default MemberAccessPopup;
